import type { ComponentProps, FC } from "react";

import styled from "styled-components";

import { Custominsights, ArrowDown } from "@similarweb/icons";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { Favicon } from "../../ui/layout/favicon";

const TrademarkContent = styled.div`
    display: flex;
    align-items: center;
    background-color: ${colorsPalettes.carbon[25]};
    border-radius: 8px;
    padding: 4px 8px 4px 4px;
    gap: 8px;
    cursor: pointer;
    z-index: 2;
`;

const TrademarkIconContainer = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: ${colorsPalettes.carbon[0]};
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${colorsPalettes.carbon[50]};
`;

export const ComboboxStyled = styled(Combobox)`
    && {
        z-index: 1054;
    }
`;

const TrademarkInfo = (props: ComponentProps<typeof Stack>) => <Stack {...props} />;

const TrademarkTitle = ({ fontVariant = "chip1TopRow", ...props }: Partial<ComponentProps<typeof Text>>) => (
    <Text
        fontVariant={fontVariant}
        {...props}
    />
);

const TrademarkSubtitle = ({ fontVariant = "queryBar1", ...props }: Partial<ComponentProps<typeof Text>>) => (
    <Text
        fontVariant={fontVariant}
        {...props}
    />
);

export const MagicBackground = styled.div<{ $active: boolean }>`
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    display: ${({ $active }) => ($active ? "block" : "none")};
    background: ${rgba(colorsPalettes.carbon["500"], 0.7)};
    z-index: 1;
`;

export const TrademarkEmptyState: FC<{ ref?: React.Ref<HTMLDivElement> }> = ({ ref }) => {
    const translate = useTranslation();
    return (
        <TrademarkContent ref={ref}>
            <TrademarkIconContainer>
                <Custominsights color={colorsPalettes.carbon[200]} />
            </TrademarkIconContainer>
            <TrademarkInfo>
                <TrademarkTitle>
                    {translate("monitor_and_protect.component.trademark_select.empty_state.title")}
                </TrademarkTitle>
                <TrademarkSubtitle>
                    {translate("monitor_and_protect.component.trademark_select.empty_state.subtitle")}
                </TrademarkSubtitle>
            </TrademarkInfo>
            <ArrowDown
                size="sm"
                color={colorsPalettes.carbon[200]}
            />
        </TrademarkContent>
    );
};

export interface ITrademarkSelectorData {
    id: string;
    name: string;
    domain: string;
}

export const TrademarkSelectedState: FC<{
    trademark: ITrademarkSelectorData;
    ref?: React.Ref<HTMLDivElement>;
}> = ({ trademark, ref }) => {
    return (
        <TrademarkContent ref={ref}>
            <TrademarkIconContainer>
                <Favicon
                    iconUrl={getFaviconUrl(trademark.domain)}
                    alt={trademark.domain}
                    size={32}
                />
            </TrademarkIconContainer>
            <TrademarkTitle>{trademark.name}</TrademarkTitle>
            <ArrowDown
                size="sm"
                color={colorsPalettes.carbon[200]}
            />
        </TrademarkContent>
    );
};
